import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ChevronRight, AlertCircle, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, Search, X } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { useDevIssues, useDevIssueReporters, useDevIssueAssignees } from "@/features/dev_issues/hooks/useDevIssues";
import { Spinner } from "@/components/ui/Spinner";
import { FilterMultiSelect } from "@/components/ui/FilterMultiSelect";
import { IssueStatusBadge } from "@/features/dev_issues/components/IssueStatusBadge";
import { IssuePriorityBadge } from "@/features/dev_issues/components/IssuePriorityBadge";
import { ReportIssueModal } from "@/features/dev_issues/components/ReportIssueModal";
import {
  ISSUE_STATUS_LABELS,
  ISSUE_CATEGORY_LABELS,
  ISSUE_PRIORITY_LABELS,
} from "@/features/dev_issues/types";
import type { IssueStatus, IssueCategory, IssuePriority } from "@/features/dev_issues/types";

type SortKey = "title" | "status" | "priority" | "category" | "reporter_name" | "assignee_name" | "created_at" | "resolved_at";
type SortDir = "asc" | "desc";

const SESSION_KEY = "devIssues.filters";

type FiltersState = {
  filterStatus: IssueStatus | "";
  filterPriority: IssuePriority | "";
  filterCategory: IssueCategory | "";
  filterTitle: string;
  filterReporter: string[];
  filterAssignee: string;
  filterMine: boolean;
  sortKey: SortKey;
  sortDir: SortDir;
};

function readSession(): Partial<FiltersState> {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeSession(patch: Partial<FiltersState>) {
  try {
    const current = readSession();
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ ...current, ...patch }));
  } catch {
    // ignore
  }
}


function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function DevIssuesListPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isDev = user?.is_developer || user?.is_ceo;

  const session = useMemo(readSession, []);
  const [filterStatus, setFilterStatusRaw] = useState<IssueStatus | "">(session.filterStatus ?? "");
  const [filterPriority, setFilterPriorityRaw] = useState<IssuePriority | "">(session.filterPriority ?? "");
  const [filterCategory, setFilterCategoryRaw] = useState<IssueCategory | "">(session.filterCategory ?? "");
  const [filterTitle, setFilterTitleRaw] = useState<string>(session.filterTitle ?? "");
  const [debouncedTitle, setDebouncedTitle] = useState<string>(session.filterTitle ?? "");
  const [filterReporter, setFilterReporterRaw] = useState<string[]>(() => {
    // Older sessions stored a single reporter id as a string.
    const raw: unknown = session.filterReporter;
    if (Array.isArray(raw)) return raw.filter((v): v is string => typeof v === "string");
    return typeof raw === "string" && raw ? [raw] : [];
  });
  const [filterAssignee, setFilterAssigneeRaw] = useState<string>(session.filterAssignee ?? "");
  const [filterMine, setFilterMineRaw] = useState<boolean>(session.filterMine ?? false);
  const [reportOpen, setReportOpen] = useState(false);
  const [sortKey, setSortKeyRaw] = useState<SortKey>(session.sortKey ?? "status");
  const [sortDir, setSortDirRaw] = useState<SortDir>(session.sortDir ?? "asc");
  const [page, setPage] = useState(1);

  const setFilterStatus = useCallback((v: IssueStatus | "") => { setFilterStatusRaw(v); writeSession({ filterStatus: v }); setPage(1); }, []);
  const setFilterPriority = useCallback((v: IssuePriority | "") => { setFilterPriorityRaw(v); writeSession({ filterPriority: v }); setPage(1); }, []);
  const setFilterCategory = useCallback((v: IssueCategory | "") => { setFilterCategoryRaw(v); writeSession({ filterCategory: v }); setPage(1); }, []);
  const setFilterTitle = useCallback((v: string) => { setFilterTitleRaw(v); writeSession({ filterTitle: v }); }, []);
  const setFilterReporter = useCallback((v: string[]) => { setFilterReporterRaw(v); writeSession({ filterReporter: v }); setPage(1); }, []);
  const setFilterAssignee = useCallback((v: string) => { setFilterAssigneeRaw(v); writeSession({ filterAssignee: v }); setPage(1); }, []);

  // Debounce the title input so we don't fire a request on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedTitle(filterTitle); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [filterTitle]);
  const setFilterMine = useCallback((updater: boolean | ((prev: boolean) => boolean)) => {
    setFilterMineRaw((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      writeSession({ filterMine: next });
      return next;
    });
    setPage(1);
  }, []);
  const setSortKey = useCallback((v: SortKey) => { setSortKeyRaw(v); writeSession({ sortKey: v }); setPage(1); }, []);
  const setSortDir = useCallback((updater: SortDir | ((prev: SortDir) => SortDir)) => {
    setSortDirRaw((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      writeSession({ sortDir: next });
      return next;
    });
  }, []);

  const { data, isLoading } = useDevIssues({
    status: filterStatus || undefined,
    priority: filterPriority || undefined,
    category: filterCategory || undefined,
    title: debouncedTitle.trim() || undefined,
    reporter: (isDev && filterReporter.join(",")) || undefined,
    assignee: (isDev && filterAssignee) || undefined,
    my_issues: (isDev && filterMine) || undefined,
    page,
    ordering: sortKey,
    ordering_dir: sortDir,
  });
  const { data: reporters } = useDevIssueReporters(!!isDev);
  const reporterOptions = useMemo(
    () => (reporters ?? []).map((r) => ({ value: r.id, label: r.name })),
    [reporters],
  );
  const { data: assignees } = useDevIssueAssignees(!!isDev);

  const issues = data?.issues ?? [];
  const meta = data?.meta;

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "created_at" ? "desc" : "asc");
    }
  }

  function SortIcon({ col }: { col: SortKey }) {
    if (sortKey !== col) return <ChevronsUpDown size={12} className="ml-1 opacity-40" />;
    return sortDir === "asc"
      ? <ChevronUp size={12} className="ml-1 text-[var(--color-primary)]" />
      : <ChevronDown size={12} className="ml-1 text-[var(--color-primary)]" />;
  }

  function ThSort({ col, label, cls }: { col: SortKey; label: string; cls?: string }) {
    return (
      <th
        className={`text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer select-none hover:text-gray-800 dark:hover:text-gray-200 ${cls ?? ""}`}
        onClick={() => handleSort(col)}
      >
        <span className="inline-flex items-center">
          {label}
          <SortIcon col={col} />
        </span>
      </th>
    );
  }

  const selectCls =
    "border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] w-full sm:w-auto min-w-0";

  const hasActiveFilters = filterStatus || filterPriority || filterCategory || filterTitle || filterReporter.length > 0 || filterAssignee || filterMine;

  return (
    <div className="flex flex-col gap-4 sm:gap-5 w-full">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
            Zgłoszenia techniczne
          </h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-1" />
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {isDev ? "Wszystkie zgłoszenia w systemie" : "Zgłoszenia, w których uczestniczysz"}
          </p>
        </div>
        <button
          onClick={() => setReportOpen(true)}
          aria-label="Zgłoś problem"
          className="flex-shrink-0 flex items-center gap-2 px-3 sm:px-4 py-2 bg-[var(--color-primary)] text-white text-sm font-medium rounded-lg hover:bg-[var(--color-primary-hover)] transition-colors"
        >
          <Plus size={16} />
          <span className="hidden sm:inline">Zgłoś problem</span>
          <span className="sm:hidden">Zgłoś</span>
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3 sm:items-center">
        <div className="col-span-2 sm:col-auto relative sm:w-56">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none" />
          <input
            type="text"
            value={filterTitle}
            onChange={(e) => setFilterTitle(e.target.value)}
            placeholder="Szukaj po tytule…"
            className={`${selectCls} sm:w-full pl-8 ${filterTitle ? "pr-8" : ""}`}
          />
          {filterTitle && (
            <button
              type="button"
              onClick={() => setFilterTitle("")}
              aria-label="Wyczyść wyszukiwanie"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <X size={15} />
            </button>
          )}
        </div>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as IssueStatus | "")}
          className={selectCls}
        >
          <option value="">Wszystkie statusy</option>
          {Object.entries(ISSUE_STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>

        <select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value as IssuePriority | "")}
          className={selectCls}
        >
          <option value="">Wszystkie priorytety</option>
          {Object.entries(ISSUE_PRIORITY_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>

        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value as IssueCategory | "")}
          className={selectCls}
        >
          <option value="">Wszystkie kategorie</option>
          {Object.entries(ISSUE_CATEGORY_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>

        {isDev && (
          <FilterMultiSelect
            label="Zgłaszający"
            options={reporterOptions}
            selected={filterReporter}
            onChange={setFilterReporter}
            searchable
          />
        )}

        {isDev && (
          <select
            value={filterAssignee}
            onChange={(e) => setFilterAssignee(e.target.value)}
            className={selectCls}
          >
            <option value="">Wszyscy przydzieleni</option>
            {(assignees ?? []).map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        )}

        {isDev && (
          <button
            onClick={() => setFilterMine((v) => !v)}
            className={`text-sm px-3 py-1.5 rounded-lg border transition-colors ${
              filterMine
                ? "bg-violet-100 dark:bg-violet-900/40 border-violet-300 dark:border-violet-700 text-violet-700 dark:text-violet-300 font-medium"
                : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            }`}
          >
            Moje
          </button>
        )}

        {hasActiveFilters && (
          <button
            onClick={() => { setFilterStatus(""); setFilterPriority(""); setFilterCategory(""); setFilterTitle(""); setFilterReporter([]); setFilterAssignee(""); setFilterMine(false); }}
            className="col-span-2 sm:col-auto text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-left sm:text-center"
          >
            Wyczyść filtry
          </button>
        )}
      </div>

      {/* List */}
      <div className="bg-white dark:bg-gray-800 overflow-hidden shadow-sm rounded-lg sm:rounded-none">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner />
          </div>
        ) : issues.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400 dark:text-gray-500">
            <AlertCircle size={36} className="text-gray-300 dark:text-gray-600" />
            <p className="text-sm">Brak zgłoszeń spełniających kryteria.</p>
          </div>
        ) : (
          <>
            {/* Mobile: card list */}
            <ul className="sm:hidden divide-y divide-gray-100 dark:divide-gray-700">
              {issues.map((issue) => (
                <li
                  key={issue.id}
                  onClick={() => navigate(`/dev-issues/${issue.id}`)}
                  className="px-4 py-3 active:bg-gray-50 dark:active:bg-gray-700/30 cursor-pointer transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                      <span className="font-medium text-sm text-gray-900 dark:text-gray-100 line-clamp-2 leading-snug">
                        {issue.title}
                      </span>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <IssueStatusBadge status={issue.status} />
                        {issue.priority && <IssuePriorityBadge priority={issue.priority} />}
                        {issue.category && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {ISSUE_CATEGORY_LABELS[issue.category]}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-400 dark:text-gray-500">
                        <span>{formatDate(issue.created_at)}</span>
                        {issue.comments_count > 0 && (
                          <span>
                            {issue.comments_count} {issue.comments_count === 1 ? "komentarz" : "komentarzy"}
                          </span>
                        )}
                        {issue.assignee_name && (
                          <span className="inline-flex items-center text-xs font-medium text-violet-700 dark:text-violet-300">
                            → {issue.assignee_name}
                          </span>
                        )}
                      </div>
                      {isDev && issue.reporter_name && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          Zgłoszone przez: {issue.reporter_name}
                        </span>
                      )}
                    </div>
                    <ChevronRight size={18} className="flex-shrink-0 text-gray-300 dark:text-gray-600 mt-1" />
                  </div>
                </li>
              ))}
            </ul>

            {/* Desktop: table */}
            <table className="w-full text-xs hidden sm:table">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
                <ThSort col="title" label="Tytuł" cls="px-4 py-2" />
                <ThSort col="status" label="Status" cls="hidden sm:table-cell" />
                <ThSort col="priority" label="Priorytet" cls="hidden md:table-cell" />
                <ThSort col="category" label="Kategoria" cls="hidden lg:table-cell" />
                {isDev && (
                  <ThSort col="reporter_name" label="Zgłaszający" cls="hidden lg:table-cell" />
                )}
                <ThSort col="assignee_name" label="Przydzielony" cls="hidden lg:table-cell" />
                <ThSort col="created_at" label="Data" cls="hidden md:table-cell" />
                <ThSort col="resolved_at" label="Zamknięto" cls="hidden lg:table-cell" />
                <th className="w-8 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />
              </tr>
            </thead>
            <tbody>
              {issues.map((issue) => (
                <tr
                  key={issue.id}
                  onClick={() => navigate(`/dev-issues/${issue.id}`)}
                  onMouseDown={(e) => { if (e.button === 1) { e.preventDefault(); window.open(`/dev-issues/${issue.id}`, "_blank"); } }}
                  className="border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium text-gray-900 dark:text-gray-100 line-clamp-1">{issue.title}</span>
                      {issue.comments_count > 0 && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {issue.comments_count} {issue.comments_count === 1 ? "komentarz" : "komentarzy"}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 hidden sm:table-cell">
                    <IssueStatusBadge status={issue.status} />
                  </td>
                  <td className="px-3 py-3 hidden md:table-cell">
                    <IssuePriorityBadge priority={issue.priority} />
                  </td>
                  <td className="px-3 py-3 hidden lg:table-cell text-gray-500 dark:text-gray-400">
                    {issue.category ? ISSUE_CATEGORY_LABELS[issue.category] : "—"}
                  </td>
                  {isDev && (
                    <td className="px-3 py-3 hidden lg:table-cell text-gray-600 dark:text-gray-300">
                      {issue.reporter_name ?? "—"}
                    </td>
                  )}
                  <td className="px-3 py-3 hidden lg:table-cell">
                    {issue.assignee_name ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-violet-700 dark:text-violet-300 bg-violet-100 dark:bg-violet-900/40 px-2 py-0.5 rounded-full">
                        {issue.assignee_name}
                      </span>
                    ) : (
                      <span className="text-gray-300 dark:text-gray-600">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3 hidden md:table-cell text-gray-400 dark:text-gray-500">
                    {formatDate(issue.created_at)}
                  </td>
                  <td className="px-3 py-3 hidden lg:table-cell text-gray-400 dark:text-gray-500">
                    {issue.resolved_at ? formatDate(issue.resolved_at) : <span className="text-gray-300 dark:text-gray-600">—</span>}
                  </td>
                  <td className="pr-3 py-3 text-gray-400 dark:text-gray-500">
                    <ChevronRight size={16} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </div>

      {meta && meta.total_pages > 1 && (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 px-1">
          <span className="text-xs text-gray-400 dark:text-gray-500">
            Strona {meta.page} z {meta.total_pages} ({meta.count} zgłoszeń)
          </span>
          <div className="flex items-center gap-2 justify-between sm:justify-end">
            <button
              disabled={meta.page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="flex-1 sm:flex-none flex items-center justify-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              <ChevronLeft size={14} />
              Poprzednia
            </button>
            <button
              disabled={meta.page >= meta.total_pages}
              onClick={() => setPage((p) => p + 1)}
              className="flex-1 sm:flex-none flex items-center justify-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              Następna
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      <ReportIssueModal open={reportOpen} onClose={() => setReportOpen(false)} />
    </div>
  );
}
